/* ── Baby Chronicle — AI Insights ─────────────────────────────────────────────── */
'use strict';

// ── Storage keys (reads from all three trackers) ──────────────────────────
const FT_KEY     = 'ft_sessions';
const DT_KEY     = 'dt_sessions';
const BL_KEY     = 'bl_logs';
const PREFS_KEY  = 'ft_prefs';
const DT_PREFS   = 'dt_prefs';
const BRIEF_CACHE_KEY = 'bb_brief_cache';

// ── AI proxy URL (mirrors birch-ai-proxy.js resolution logic) ────────────
const LOCAL_DEV_RX = /^(localhost|127\.0\.0\.1|.*\.local)$/i;
const STAGING_WORKER = 'https://birch-ai-edge.api-staging.birchstore.com/apps/ai';
function resolveAIProxy() {
  const theme = /** @type {any} */ (window).Theme;
  if (theme?.routes?.aiProxy) return theme.routes.aiProxy;
  return LOCAL_DEV_RX.test(window.location.hostname) ? STAGING_WORKER : '/apps/ai';
}

// ── State ─────────────────────────────────────────────────────────────────
let ftSessions = [];
let dtSessions = [];
let blLogs     = [];
let babyName   = 'Baby';
let currentScreen = 'home';
let currentPeriod = 'today';
let chatHistory   = [];

// ── DOM helpers ───────────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function el(tag, cls, html) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

// ── Time utilities ────────────────────────────────────────────────────────
function msToHMM(ms) {
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatTimeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem === 0 ? `${h}h ago` : `${h}h ${rem}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDay(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

function dayStart(daysAgo = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return d.getTime();
}

function todayStart() { return dayStart(0); }

// ── Storage ───────────────────────────────────────────────────────────────
function loadAllData() {
  try { ftSessions = JSON.parse(localStorage.getItem(FT_KEY) || '[]'); } catch { ftSessions = []; }
  try { dtSessions = JSON.parse(localStorage.getItem(DT_KEY) || '[]'); } catch { dtSessions = []; }
  try { blLogs     = JSON.parse(localStorage.getItem(BL_KEY) || '[]'); } catch { blLogs = []; }
}

function loadBabyName() {
  try {
    const ftPrefs = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    const dtPrefs = JSON.parse(localStorage.getItem(DT_PREFS) || '{}');
    babyName = ftPrefs.babyName || dtPrefs.babyName || 'Baby';
  } catch { /* keep default */ }
}

// ── Data helpers ──────────────────────────────────────────────────────────
// Feeding: sessions may have startedAt or loggedAt
function ftTs(s) { return s.startedAt || s.endedAt || s.loggedAt || 0; }
// Sleep: sessions use startTime
function dtTs(s) { return s.startTime || 0; }
// BabyLog: uses loggedAt or startTime
function blTs(l) { return l.loggedAt || l.startTime || 0; }

function ftInRange(from, to) {
  return ftSessions.filter(s => { const t = ftTs(s); return t >= from && t < to; });
}
function dtInRange(from, to) {
  return dtSessions.filter(s => { const t = dtTs(s); return t >= from && t < to; });
}
function blInRange(from, to) {
  return blLogs.filter(l => { const t = blTs(l); return t >= from && t < to; });
}

function feedLabel(s) {
  if (s.type === 'nurse') {
    const side = s.leftMs && s.rightMs ? 'Both sides' : s.leftMs ? 'Left' : 'Right';
    return `Nurse · ${side} · ${msToHMM(s.totalMs || (s.leftMs + s.rightMs) || 0)}`;
  }
  if (s.type === 'bottle') {
    const amt = s.amountOz ? `${s.amountOz} oz` : s.amountMl ? `${s.amountMl} ml` : '';
    return `Bottle${amt ? ' · ' + amt : ''}`;
  }
  if (s.type === 'pump') {
    const total = s.totalOz ? `${s.totalOz} oz` : '';
    return `Pump${total ? ' · ' + total : ''}`;
  }
  return 'Feed';
}

// ── Live Status ───────────────────────────────────────────────────────────
function computeLiveStatus() {
  const now = Date.now();
  const today = todayStart();

  // Sleep state
  const sortedSleep = [...dtSessions].sort((a, b) => dtTs(b) - dtTs(a));
  const lastSleep = sortedSleep[0];
  let sleepStatus = 'No sleep logged';
  if (lastSleep) {
    if (!lastSleep.endTime) {
      const dur = now - lastSleep.startTime;
      sleepStatus = `Sleeping · ${msToHMM(dur)}`;
    } else {
      const awake = now - lastSleep.endTime;
      sleepStatus = `Awake · ${msToHMM(awake)} ago`;
    }
  }

  // Last feed
  const sortedFeeds = [...ftSessions].sort((a, b) => ftTs(b) - ftTs(a));
  const lastFeed = sortedFeeds[0];
  let feedStatus = 'No feeds logged';
  if (lastFeed) {
    feedStatus = `${formatTimeAgo(ftTs(lastFeed))} · ${feedLabel(lastFeed)}`;
  }

  // Diapers today
  const todayBl = blInRange(today, today + 86400000);
  const diapers = todayBl.filter(l => l.type === 'diaper');
  const wetCount  = diapers.filter(l => l.diaperType === 'wet'  || l.diaperType === 'both').length;
  const dirtyCount = diapers.filter(l => l.diaperType === 'dirty' || l.diaperType === 'both').length;
  let diaperStatus = diapers.length === 0 ? 'None logged today' : '';
  if (diapers.length > 0) {
    const parts = [];
    if (wetCount > 0)   parts.push(`${wetCount} wet`);
    if (dirtyCount > 0) parts.push(`${dirtyCount} dirty`);
    if (parts.length === 0) parts.push(`${diapers.length} total`);
    diaperStatus = parts.join(', ');
  }

  // Tummy time today
  const tummyToday = todayBl.filter(l => l.type === 'tummy_time' && l.durationMin);
  const tummyMins = tummyToday.reduce((s, l) => s + (l.durationMin || 0), 0);
  const tummyStatus = tummyMins > 0 ? `${Math.round(tummyMins)} min today` : 'None logged today';

  // Wellness dot
  let wellness = 'green';
  const feedGap = lastFeed ? (now - ftTs(lastFeed)) : Infinity;
  if (feedGap > 5 * 3600000) wellness = 'red';
  else if (feedGap > 3 * 3600000 || diapers.length < 3) wellness = 'amber';

  return { sleepStatus, feedStatus, diaperStatus, tummyStatus, wellness };
}

function renderLiveStatus() {
  const { sleepStatus, feedStatus, diaperStatus, tummyStatus, wellness } = computeLiveStatus();
  $('#bb-status-sleep').textContent  = sleepStatus;
  $('#bb-status-feed').textContent   = feedStatus;
  $('#bb-status-diaper').textContent = diaperStatus;
  $('#bb-status-tummy').textContent  = tummyStatus;

  const dot = $('#bb-wellness-dot');
  dot.className = `bb-wellness-dot bb-wellness-dot--${wellness}`;
  dot.title = wellness === 'green' ? 'All good' : wellness === 'amber' ? 'Check in soon' : 'Needs attention';

  $('#bb-topbar-baby-name').textContent = babyName;
}

// ── Today metrics ─────────────────────────────────────────────────────────
function computeToday() {
  const today = todayStart();
  const tomorrow = today + 86400000;

  const feeds  = ftInRange(today, tomorrow);
  const sleeps = dtInRange(today, tomorrow);
  const logs   = blInRange(today, tomorrow);

  const sleepMs = sleeps.reduce((s, sl) => {
    if (sl.endTime) return s + ((sl.endTime - sl.startTime) || sl.durationMin * 60000 || 0);
    const dur = sl.durationMin ? sl.durationMin * 60000 : 0;
    return s + dur;
  }, 0);

  const diapers = logs.filter(l => l.type === 'diaper').length;

  return { feeds, sleeps, logs, sleepMs, diapers };
}

function renderTodayMetrics() {
  const { feeds, sleepMs, diapers } = computeToday();
  $('#bb-tile-sleep').textContent   = sleepMs > 0 ? msToHMM(sleepMs) : '—';
  $('#bb-tile-feeds').textContent   = feeds.length > 0 ? String(feeds.length) : '—';
  $('#bb-tile-diapers').textContent = diapers > 0 ? String(diapers) : '—';
}

// ── 24h Timeline ──────────────────────────────────────────────────────────
function renderTimeline() {
  const track = $('#bb-timeline-track');
  if (!track) return;
  track.innerHTML = '';

  const now   = Date.now();
  const start = now - 24 * 3600000;
  const span  = 24 * 3600000;

  function pct(ts) { return Math.max(0, Math.min(100, ((ts - start) / span) * 100)); }

  // Sleep blocks
  dtSessions
    .filter(s => dtTs(s) >= start || (s.endTime && s.endTime >= start))
    .forEach(s => {
      const sStart = Math.max(s.startTime, start);
      const sEnd   = s.endTime ? Math.min(s.endTime, now) : now;
      if (sEnd <= sStart) return;
      const bar = document.createElement('div');
      bar.className = 'bb-tl-sleep';
      bar.style.left  = `${pct(sStart)}%`;
      bar.style.width = `${pct(sEnd) - pct(sStart)}%`;
      bar.title = `Sleep ${formatTime(sStart)}–${formatTime(sEnd)}`;
      track.appendChild(bar);
    });

  // Feed events
  ftSessions
    .filter(s => ftTs(s) >= start)
    .forEach(s => {
      const dot = document.createElement('div');
      dot.className = 'bb-tl-feed';
      dot.style.left  = `${pct(ftTs(s))}%`;
      dot.title = `${feedLabel(s)} at ${formatTime(ftTs(s))}`;
      track.appendChild(dot);
    });

  // Diaper events
  blLogs
    .filter(l => l.type === 'diaper' && blTs(l) >= start)
    .forEach(l => {
      const dot = document.createElement('div');
      dot.className = 'bb-tl-diaper';
      dot.style.left  = `${pct(blTs(l))}%`;
      dot.title = `Diaper at ${formatTime(blTs(l))}`;
      track.appendChild(dot);
    });
}

// ── Today insight cards ───────────────────────────────────────────────────
function renderInsightCards() {
  const list = $('#bb-insights-list');
  if (!list) return;
  list.innerHTML = '';

  const today = todayStart();
  const tomorrow = today + 86400000;
  const week  = today - 6 * 86400000;

  const todayFeeds  = ftInRange(today, tomorrow);
  const weekFeeds   = ftInRange(week, tomorrow);
  const todaySleeps = dtInRange(today, tomorrow);
  const weekSleeps  = dtInRange(week, tomorrow);
  const todayBl     = blInRange(today, tomorrow);

  const insights = [];

  // Feed gap insight
  if (todayFeeds.length >= 2) {
    const sorted = [...todayFeeds].sort((a, b) => ftTs(a) - ftTs(b));
    const gaps = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push(ftTs(sorted[i]) - ftTs(sorted[i - 1]));
    }
    const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    const h = Math.round(avgGap / 3600000 * 10) / 10;
    insights.push({
      type: 'pattern',
      headline: `${h}h average feed gap today`,
      text: h < 1.5 ? 'Feeding frequently — baby may be going through a growth spurt.' : h > 4 ? 'Longer gaps between feeds today.' : 'Feed intervals look typical.',
    });
  }

  // Sleep consistency insight
  if (weekSleeps.length >= 3) {
    const totalMinPerDay = Array.from({ length: 7 }, (_, i) => {
      const from = today - i * 86400000;
      const to   = from + 86400000;
      return dtInRange(from, to).reduce((s, sl) => s + (sl.durationMin || 0), 0);
    }).filter(m => m > 0);
    if (totalMinPerDay.length >= 3) {
      const avg = totalMinPerDay.reduce((s, m) => s + m, 0) / totalMinPerDay.length;
      const h = Math.round(avg / 60 * 10) / 10;
      insights.push({
        type: 'pattern',
        headline: `${h}h average daily sleep (last week)`,
        text: avg < 10 * 60 ? 'Sleep totals are a bit low this week — watch for overtired cues.' : avg > 18 * 60 ? 'Great sleep time this week!' : 'Sleep totals are within normal range.',
      });
    }
  }

  // Diaper pattern
  const diapers = todayBl.filter(l => l.type === 'diaper');
  if (diapers.length > 0 && diapers.length < 4) {
    insights.push({
      type: 'flag',
      headline: `Only ${diapers.length} diaper${diapers.length > 1 ? 's' : ''} today`,
      text: 'Fewer than 4-6 wet diapers per day can be a sign of dehydration. Keep an eye on feeds.',
    });
  } else if (diapers.length >= 6) {
    insights.push({
      type: 'tip',
      headline: 'Solid diaper count today',
      text: `${diapers.length} diapers logged — good hydration signal.`,
    });
  }

  // Tummy time suggestion
  const tummyToday = todayBl.filter(l => l.type === 'tummy_time' && l.durationMin);
  const tummyMins  = tummyToday.reduce((s, l) => s + l.durationMin, 0);
  if (tummyMins === 0) {
    insights.push({
      type: 'tip',
      headline: 'No tummy time logged yet',
      text: 'Aim for 30 minutes of tummy time spread throughout the day to build neck and shoulder strength.',
    });
  } else if (tummyMins >= 30) {
    insights.push({
      type: 'tip',
      headline: `${Math.round(tummyMins)} min tummy time today`,
      text: 'Great job hitting the 30-minute tummy time goal!',
    });
  }

  if (insights.length === 0) {
    const empty = el('div', 'bb-insight-card');
    empty.innerHTML = `<div class="bb-insight-card__body"><div class="bb-insight-card__headline">Keep logging to unlock insights</div><div class="bb-insight-card__text">Log a few days of feeding, sleep, and diapers to see patterns here.</div></div>`;
    list.appendChild(empty);
    return;
  }

  const iconMap = {
    pattern: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
    tip:     `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
    flag:    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
  };

  insights.forEach(ins => {
    const card = el('div', 'bb-insight-card');
    card.innerHTML = `
      <div class="bb-insight-card__icon bb-insight-card__icon--${ins.type}">${iconMap[ins.type]}</div>
      <div class="bb-insight-card__body">
        <div class="bb-insight-card__headline">${ins.headline}</div>
        <div class="bb-insight-card__text">${ins.text}</div>
      </div>`;
    list.appendChild(card);
  });
}

// ── Week table ────────────────────────────────────────────────────────────
function renderWeekTable() {
  const tbody = $('#bb-week-tbody');
  if (!tbody) return;
  tbody.innerHTML = '';

  let bestDay = null;
  let bestScore = -1;

  for (let i = 6; i >= 0; i--) {
    const from   = dayStart(i);
    const to     = from + 86400000;
    const feeds  = ftInRange(from, to);
    const sleeps = dtInRange(from, to);
    const logs   = blInRange(from, to);
    const diapers = logs.filter(l => l.type === 'diaper').length;
    const sleepMin = sleeps.reduce((s, sl) => s + (sl.durationMin || 0), 0);
    const sleepH   = sleepMin >= 60 ? `${Math.round(sleepMin / 60 * 10) / 10}h` : sleepMin > 0 ? `${sleepMin}m` : '—';

    const score = feeds.length * 2 + (sleepMin / 60) + diapers;
    if (score > bestScore && (feeds.length > 0 || sleeps.length > 0)) {
      bestScore = score;
      bestDay = { label: formatDay(from + 3600000), feeds: feeds.length, sleepH, diapers };
    }

    const tr = document.createElement('tr');
    if (i === 0) tr.className = 'bb-week-row--today';
    tr.innerHTML = `<td>${i === 0 ? 'Today' : formatDay(from + 3600000)}</td><td>${sleepH}</td><td>${feeds.length > 0 ? feeds.length : '—'}</td><td>${diapers > 0 ? diapers : '—'}</td>`;
    tbody.appendChild(tr);
  }

  if (bestDay) {
    const card = $('#bb-best-day-card');
    if (card) {
      card.style.display = '';
      $('#bb-best-day-value').textContent = bestDay.label;
      $('#bb-best-day-reason').textContent = `${bestDay.feeds} feeds · ${bestDay.sleepH} sleep · ${bestDay.diapers} diapers`;
    }
  }
}

// ── Month stats ───────────────────────────────────────────────────────────
function renderMonthStats() {
  const container = $('#bb-month-stats');
  if (!container) return;
  container.innerHTML = '';

  const from = Date.now() - 30 * 86400000;
  const to   = Date.now();

  const feeds  = ftInRange(from, to);
  const sleeps = dtInRange(from, to);
  const logs   = blInRange(from, to);

  const totalSleepMin = sleeps.reduce((s, sl) => s + (sl.durationMin || 0), 0);
  const avgSleepH = sleeps.length > 0 ? Math.round(totalSleepMin / 30 / 60 * 10) / 10 : 0;
  const totalDiapers = logs.filter(l => l.type === 'diaper').length;
  const avgFeeds = Math.round(feeds.length / 30 * 10) / 10;

  const stats = [
    { label: 'Avg daily sleep', value: avgSleepH > 0 ? `${avgSleepH}h` : '—', sub: 'last 30 days' },
    { label: 'Total feeds', value: feeds.length > 0 ? String(feeds.length) : '—', sub: 'last 30 days' },
    { label: 'Avg feeds/day', value: avgFeeds > 0 ? String(avgFeeds) : '—', sub: 'last 30 days' },
    { label: 'Total diapers', value: totalDiapers > 0 ? String(totalDiapers) : '—', sub: 'last 30 days' },
  ];

  stats.forEach(s => {
    const div = el('div', 'bb-month-stat');
    div.innerHTML = `<div class="bb-month-stat__label">${s.label}</div><div class="bb-month-stat__value">${s.value}</div><div class="bb-month-stat__sub">${s.sub}</div>`;
    container.appendChild(div);
  });
}

// ── AI Brief calls ────────────────────────────────────────────────────────
function buildSummaryData() {
  const today = todayStart();
  const tomorrow = today + 86400000;
  const week = today - 6 * 86400000;

  const todayFeeds  = ftInRange(today, tomorrow);
  const todaySleeps = dtInRange(today, tomorrow);
  const todayBl     = blInRange(today, tomorrow);
  const weekFeeds   = ftInRange(week, tomorrow);
  const weekSleeps  = dtInRange(week, tomorrow);

  const diapers = todayBl.filter(l => l.type === 'diaper').length;
  const tummyMin = todayBl.filter(l => l.type === 'tummy_time' && l.durationMin).reduce((s, l) => s + l.durationMin, 0);
  const sleepMin = todaySleeps.reduce((s, sl) => s + (sl.durationMin || 0), 0);

  const avgSleepH7 = (() => {
    const totals = Array.from({ length: 7 }, (_, i) => {
      const f = today - i * 86400000;
      return dtInRange(f, f + 86400000).reduce((s, sl) => s + (sl.durationMin || 0), 0);
    }).filter(m => m > 0);
    return totals.length > 0 ? Math.round(totals.reduce((s, m) => s + m, 0) / totals.length / 60 * 10) / 10 : 0;
  })();

  return {
    todayFeeds: todayFeeds.length,
    todaySleepMin: sleepMin,
    todayDiapers: diapers,
    todayTummyMin: Math.round(tummyMin),
    weekFeeds: weekFeeds.length,
    avgDailySleepH7: avgSleepH7,
    lastFeedAgo: (() => {
      const sorted = [...ftSessions].sort((a, b) => ftTs(b) - ftTs(a));
      return sorted[0] ? Math.round((Date.now() - ftTs(sorted[0])) / 60000) : null;
    })(),
  };
}

async function fetchDailyBrief(periodKey = 'today') {
  const cacheKey = `${BRIEF_CACHE_KEY}_${periodKey}_${new Date().toDateString()}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch { /* ignore */ }

  const spinnerId = periodKey === 'today' ? '#bb-brief-spinner' : periodKey === 'week' ? '#bb-week-spinner' : '#bb-month-spinner';
  const spinner = $(spinnerId);
  if (spinner) spinner.classList.add('bb-brief-card__spinner--active');

  try {
    const summaryData = buildSummaryData();
    const resp = await fetch(`${resolveAIProxy()}/brief/daily`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ babyName, period: periodKey, summaryData }),
    });
    if (!resp.ok) {
      const errText = await resp.text().catch(() => resp.status);
      console.error('[Baby Chronicle] brief/daily error', resp.status, errText);
      throw new Error('AI error');
    }
    const data = await resp.json();
    try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { /* ignore */ }
    return data;
  } catch (err) {
    console.error('[Baby Chronicle] fetchDailyBrief failed:', err);
    return { narrative: 'Unable to load AI brief right now.', tip: '' };
  } finally {
    if (spinner) spinner.classList.remove('bb-brief-card__spinner--active');
  }
}

async function loadAndRenderBrief(periodKey) {
  const narrativeId = periodKey === 'today' ? '#bb-brief-narrative' : periodKey === 'week' ? '#bb-week-narrative' : '#bb-month-narrative';
  const tipId       = periodKey === 'today' ? '#bb-brief-tip'       : periodKey === 'week' ? '#bb-week-tip'       : '#bb-month-tip';
  const tipPaneId   = periodKey === 'today' ? '#bb-tip-today'       : periodKey === 'week' ? '#bb-tip-week'       : '#bb-tip-month';

  const { narrative, tip } = await fetchDailyBrief(periodKey);
  const narrativeEl = $(narrativeId);
  if (narrativeEl) narrativeEl.textContent = narrative;

  const tipEl = $(tipId);
  const tipPane = $(tipPaneId);
  if (tipEl && tipPane && tip) {
    tipEl.textContent = tip;
    tipPane.style.display = '';
  }
}

// ── Ask / Chat ────────────────────────────────────────────────────────────
function appendChatMsg(text, role) {
  const list = $('#bb-chat-list');
  if (!list) return null;
  const div = el('div', `bb-chat-msg bb-chat-msg--${role}`);
  div.textContent = text;
  list.appendChild(div);
  div.scrollIntoView({ behavior: 'smooth', block: 'end' });
  return div;
}

function appendLoadingDots() {
  const list = $('#bb-chat-list');
  if (!list) return null;
  const div = el('div', 'bb-chat-msg bb-chat-msg--loading');
  div.innerHTML = `<div class="bb-chat-dots"><span></span><span></span><span></span></div>`;
  list.appendChild(div);
  div.scrollIntoView({ behavior: 'smooth', block: 'end' });
  return div;
}

async function sendChatMessage(question) {
  if (!question.trim()) return;

  const input  = $('#bb-chat-input');
  const sendBtn = $('#bb-chat-send');
  if (input) input.value = '';
  if (sendBtn) sendBtn.disabled = true;

  chatHistory.push({ role: 'user', text: question });
  appendChatMsg(question, 'user');

  const loadingEl = appendLoadingDots();

  try {
    const context = buildSummaryData();
    const resp = await fetch(`${resolveAIProxy()}/brief/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ babyName, question, context }),
    });
    if (!resp.ok) console.error('[Baby Chronicle] brief/ask error', resp.status);
    const data = resp.ok ? await resp.json() : { answer: 'Unable to answer right now.' };
    const answer = data.answer || 'Unable to answer right now.';
    chatHistory.push({ role: 'ai', text: answer });
    if (loadingEl) loadingEl.remove();
    appendChatMsg(answer, 'ai');
  } catch {
    if (loadingEl) loadingEl.remove();
    appendChatMsg('Something went wrong. Please try again.', 'ai');
  } finally {
    if (sendBtn) sendBtn.disabled = false;
  }
}

// ── Export / Print ────────────────────────────────────────────────────────
function buildExportHTML() {
  const from = Date.now() - 7 * 86400000;
  const to   = Date.now();

  const feeds  = ftInRange(from, to);
  const sleeps = dtInRange(from, to);
  const logs   = blInRange(from, to);

  const rows = Array.from({ length: 7 }, (_, i) => {
    const f  = dayStart(6 - i);
    const t  = f + 86400000;
    const fs = ftInRange(f, t);
    const ss = dtInRange(f, t);
    const ls = blInRange(f, t);
    const sleepMin = ss.reduce((s, sl) => s + (sl.durationMin || 0), 0);
    const diapers  = ls.filter(l => l.type === 'diaper').length;
    return {
      day: formatDay(f + 3600000),
      feeds: fs.length,
      sleep: sleepMin >= 60 ? `${Math.round(sleepMin / 60 * 10) / 10}h` : sleepMin > 0 ? `${sleepMin}m` : '—',
      diapers,
    };
  });

  return `
<h1>Baby Chronicle — ${babyName}'s Report</h1>
<p><strong>Generated:</strong> ${new Date().toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
<p><strong>Period:</strong> Last 7 days</p>
<h2>Daily Summary</h2>
<table>
  <thead><tr><th>Day</th><th>Sleep</th><th>Feeds</th><th>Diapers</th></tr></thead>
  <tbody>${rows.map(r => `<tr><td>${r.day}</td><td>${r.sleep}</td><td>${r.feeds || '—'}</td><td>${r.diapers || '—'}</td></tr>`).join('')}</tbody>
</table>
<h2>Totals (last 7 days)</h2>
<table>
  <thead><tr><th>Metric</th><th>Value</th></tr></thead>
  <tbody>
    <tr><td>Total feeds</td><td>${feeds.length}</td></tr>
    <tr><td>Total sleep sessions</td><td>${sleeps.length}</td></tr>
    <tr><td>Total diapers</td><td>${logs.filter(l => l.type === 'diaper').length}</td></tr>
    <tr><td>Total tummy time</td><td>${Math.round(logs.filter(l => l.type === 'tummy_time' && l.durationMin).reduce((s, l) => s + l.durationMin, 0))} min</td></tr>
  </tbody>
</table>`;
}

function renderExportScreen() {
  const from = new Date(Date.now() - 7 * 86400000).toLocaleDateString([], { month: 'short', day: 'numeric' });
  const to   = new Date().toLocaleDateString([], { month: 'short', day: 'numeric' });
  const rangeEl = $('#bb-export-range');
  if (rangeEl) rangeEl.textContent = `${from} – ${to}`;
}

function printReport() {
  const report = $('#bb-print-report');
  if (report) report.innerHTML = buildExportHTML();
  window.print();
}

// ── Navigation ────────────────────────────────────────────────────────────
function navigate(screen) {
  if (screen === 'ask') {
    if (window.BirchAIChat) window.BirchAIChat.openWithQuery('');
    return;
  }
  if (screen === 'export') {
    printReport();
    return;
  }

  currentScreen = screen;

  $$('.bb-screen').forEach(s => s.classList.remove('bb-screen--active'));
  const active = $(`#bb-screen-${screen}`);
  if (active) active.classList.add('bb-screen--active');

  $$('.bb-nav-item').forEach(b => b.classList.remove('bb-nav-item--active'));
  const activeNav = $(`.bb-nav-item[data-screen="${screen}"]`);
  if (activeNav) activeNav.classList.add('bb-nav-item--active');

  if (screen === 'home') renderPeriod(currentPeriod);
}

function switchPeriod(period) {
  currentPeriod = period;

  $$('.bb-period-tab').forEach(t => t.classList.remove('bb-period-tab--active'));
  const active = $(`.bb-period-tab[data-period="${period}"]`);
  if (active) active.classList.add('bb-period-tab--active');

  $$('.bb-period-view').forEach(v => v.classList.remove('bb-period-view--active'));
  const activeView = $(`#bb-view-${period}`);
  if (activeView) activeView.classList.add('bb-period-view--active');

  renderPeriod(period);
}

function renderPeriod(period) {
  renderLiveStatus();
  if (period === 'today') {
    renderTodayMetrics();
    renderTimeline();
    renderInsightCards();
  } else if (period === 'week') {
    renderWeekTable();
  } else if (period === 'month') {
    renderMonthStats();
  }
}

// ── Init ──────────────────────────────────────────────────────────────────
function init() {
  loadAllData();
  loadBabyName();

  // Status card rows — tap to open the individual tracker
  $$('.bb-status-row--link').forEach(row => {
    row.addEventListener('click', () => {
      const href = row.dataset.href;
      if (href) window.location.href = href;
    });
  });

  // Nav buttons
  $$('.bb-nav-item').forEach(btn => {
    btn.addEventListener('click', () => navigate(btn.dataset.screen));
  });

  // Period tabs
  $$('.bb-period-tab').forEach(tab => {
    tab.addEventListener('click', () => switchPeriod(tab.dataset.period));
  });

  // Chip questions
  $$('.bb-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      navigate('ask');
      sendChatMessage(chip.dataset.q);
    });
  });

  // Chat send
  const sendBtn = $('#bb-chat-send');
  const chatInput = $('#bb-chat-input');
  if (sendBtn && chatInput) {
    sendBtn.addEventListener('click', () => sendChatMessage(chatInput.value));
    chatInput.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput.value); }
    });
  }

  // Export button (on the export screen card — kept for direct access)
  const exportBtn = $('#bb-export-pdf');
  if (exportBtn) exportBtn.addEventListener('click', printReport);

  // Read AI Insights buttons (one per period card)
  [
    { btnId: 'bb-read-brief-btn',  narrativeId: 'bb-brief-narrative',  spinnerId: 'bb-brief-spinner',  period: 'today' },
    { btnId: 'bb-read-week-btn',   narrativeId: 'bb-week-narrative',   spinnerId: 'bb-week-spinner',   period: 'week'  },
    { btnId: 'bb-read-month-btn',  narrativeId: 'bb-month-narrative',  spinnerId: 'bb-month-spinner',  period: 'month' },
  ].forEach(({ btnId, narrativeId, spinnerId, period }) => {
    const btn = $(`#${btnId}`);
    if (!btn) return;
    btn.addEventListener('click', () => {
      btn.classList.add('bb-read-insights-btn--loading');
      btn.textContent = 'Generating…';
      loadAndRenderBrief(period).finally(() => {
        btn.classList.remove('bb-read-insights-btn--loading');
        const narrative = $(`#${narrativeId}`);
        if (narrative && narrative.textContent.trim()) btn.style.display = 'none';
        else { btn.textContent = 'Read AI Insights'; }
      });
    });
  });

  // Desktop action buttons
  const desktopAskBtn = $('#bb-desktop-ask-btn');
  if (desktopAskBtn) desktopAskBtn.addEventListener('click', () => {
    if (window.BirchAIChat) window.BirchAIChat.openWithQuery('');
  });

  const desktopExportBtn = $('#bb-desktop-export-btn');
  if (desktopExportBtn) desktopExportBtn.addEventListener('click', printReport);

  // Initial render
  navigate('home');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
